/**
 * Pricing — /pricing
 *
 * 3-tier pricing page. Consistent with the Other Side brand: dark ink
 * background, gold accents, serif headings, generous spacing. The middle
 * plan (Emotional) is highlighted as the recommended one — a standard
 * pricing-page pattern that converts better than a uniform grid.
 *
 * When a plan's Price ID is not yet configured in Stripe env vars, its
 * card shows a subtle "presto disponibile" and its button is disabled,
 * so the page stays usable even while you're finishing Stripe setup.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { Check, Sparkles } from "lucide-react";
import { track } from "@/lib/track";

const RECOMMENDED = "emotional";

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [pRes, sRes] = await Promise.all([
                    api.get("/subscriptions/plans"),
                    user ? api.get("/subscriptions/status") : Promise.resolve({ data: null }),
                ]);
                setPlans(pRes.data.plans || []);
                setStatus(sRes.data);
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    const startCheckout = async (planId) => {
        if (!user) {
            nav(`/signup?redirect=/pricing`);
            return;
        }
        setBusy(`stripe-${planId}`);
        try {
            const { data } = await api.post("/subscriptions/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            track("subscription_checkout", { provider: "stripe", plan_id: planId });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            alert(e?.response?.data?.detail || "Checkout non disponibile al momento.");
            setBusy(null);
        }
    };

    const startPaypalCheckout = async (planId) => {
        if (!user) {
            nav(`/signup?redirect=/pricing`);
            return;
        }
        setBusy(`paypal-${planId}`);
        try {
            const { data } = await api.post("/paypal/subscriptions/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            track("subscription_checkout", { provider: "paypal", plan_id: planId });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            alert(e?.response?.data?.detail || "PayPal non disponibile al momento.");
            setBusy(null);
        }
    };

    const openPortal = async () => {
        setBusy("portal");
        try {
            const { data } = await api.post("/subscriptions/portal", {
                origin_url: window.location.origin,
            });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            alert(e?.response?.data?.detail || "Impossibile aprire il portale al momento.");
            setBusy(null);
        }
    };

    return (
        <div className="grain min-h-screen" data-testid="pricing-page">
            <Header />
            <main className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-32 animate-fade-in">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <div className="flex justify-center mb-6">
                        <BrandMark size={56} ceremonial />
                    </div>
                    <p className="label-eyebrow mb-4">Piani</p>
                    <h1 className="h-serif text-5xl md:text-6xl leading-[1.05] mb-6">
                        Scegli come <span className="italic text-ink-gold">custodirla</span>.
                    </h1>
                    <p className="text-ink-textMid text-lg leading-[1.7]">
                        Un abbonamento mensile ti dà il tempo per lasciarla tornare, quando vuoi,
                        nella forma che preferisci. Puoi cambiare o interrompere in ogni momento.
                    </p>
                </div>

                {/* Active subscription banner */}
                {status?.active && (
                    <div className="max-w-2xl mx-auto mb-12 border border-ink-gold/50 bg-ink-gold/5 rounded-sm p-5 flex items-center justify-between gap-4 animate-fade-in" data-testid="active-sub-banner">
                        <div>
                            <p className="label-eyebrow">Abbonamento attivo</p>
                            <p className="font-serif text-lg text-ink-textHigh mt-1">
                                Piano <span className="italic text-ink-gold">{statusToName(status.tier)}</span>
                                {status.cancel_at_period_end ? " · termina a fine periodo" : ""}
                            </p>
                        </div>
                        <button onClick={openPortal} disabled={busy === "portal"} className="btn-secondary" data-testid="open-portal-btn">
                            {busy === "portal" ? "…" : "Gestisci"}
                        </button>
                    </div>
                )}

                {/* 3 plan cards */}
                {loading ? (
                    <p className="text-center text-ink-textLow italic font-serif">caricando…</p>
                ) : (
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {plans.map((p) => (
                            <PlanCard
                                key={p.id}
                                plan={p}
                                recommended={p.id === RECOMMENDED}
                                currentTier={status?.tier}
                                active={status?.active && status.tier === p.id}
                                busy={busy}
                                onSubscribe={() => startCheckout(p.id)}
                                onSubscribePaypal={() => startPaypalCheckout(p.id)}
                            />
                        ))}
                    </div>
                )}

                {/* Fine print */}
                <div className="max-w-3xl mx-auto mt-20 text-center text-sm text-ink-textLow font-serif italic leading-relaxed">
                    I prezzi sono in Euro, IVA inclusa ove applicabile. L'abbonamento si rinnova
                    automaticamente al termine del periodo; puoi cancellarlo in qualsiasi momento
                    dal tuo portale di gestione, senza penali. La fatturazione è gestita da Stripe
                    o PayPal a seconda del metodo scelto.
                </div>
            </main>
        </div>
    );
}

function statusToName(tier) {
    return ({ basic: "Basic", emotional: "Emotional", premium: "Premium" })[tier] || tier;
}

function PlanCard({ plan, recommended, active, busy, currentTier, onSubscribe, onSubscribePaypal }) {
    const unavailable = !plan.available;
    const isCurrent = active;
    const stripeBusy = busy === `stripe-${plan.id}`;
    const paypalBusy = busy === `paypal-${plan.id}`;
    const anyBusy = stripeBusy || paypalBusy;
    return (
        <div
            data-testid={`plan-card-${plan.id}`}
            className={`relative card-paper transition-all duration-500 ${
                recommended
                    ? "border-ink-gold ring-1 ring-ink-gold/40 md:-translate-y-2"
                    : "hover:!border-ink-textLow"
            } ${isCurrent ? "!border-ink-gold bg-ink-gold/5" : ""}`}
        >
            {recommended && !isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ink-gold text-ink-deep text-[0.68rem] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                    <Sparkles size={11} /> Consigliato
                </div>
            )}
            {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ink-gold/90 text-ink-deep text-[0.68rem] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full">
                    Piano attuale
                </div>
            )}

            <p className="label-eyebrow mb-2">{plan.name}</p>
            <p className="font-serif text-5xl text-ink-textHigh leading-none">
                €{plan.price_eur.toFixed(2).replace(".", ",")}
                <span className="text-base text-ink-textLow italic ml-1">/ mese</span>
            </p>
            <div className="mt-4 text-xs text-ink-textLow">
                Fino a <strong className="text-ink-textMid">{plan.max_presences === 999 ? "∞" : plan.max_presences}</strong>
                {" "}{plan.max_presences === 1 ? "presenza" : "presenze"}
            </div>

            <ul className="mt-8 space-y-3 min-h-[220px]">
                {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-ink-textMid leading-relaxed">
                        <Check size={15} className="text-ink-gold mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            {unavailable ? (
                <button disabled className="w-full btn-secondary mt-8 opacity-50 cursor-not-allowed" data-testid={`plan-cta-${plan.id}`}>
                    Presto disponibile
                </button>
            ) : isCurrent ? (
                <p className="w-full mt-8 text-center text-sm text-ink-gold font-serif italic" data-testid={`plan-cta-${plan.id}`}>
                    Stai usando questo piano
                </p>
            ) : (
                <div className="mt-8 space-y-3">
                    <button
                        onClick={onSubscribe}
                        disabled={anyBusy}
                        className={`w-full ${recommended ? "btn-primary" : "btn-secondary"} disabled:opacity-50 inline-flex items-center justify-center gap-2`}
                        data-testid={`plan-cta-${plan.id}`}
                    >
                        {stripeBusy ? "…" : (
                            <>
                                <CardIcon />
                                <span>{currentTier ? "Cambia piano · Carta" : "Paga con carta"}</span>
                            </>
                        )}
                    </button>
                    <button
                        onClick={onSubscribePaypal}
                        disabled={anyBusy}
                        className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-sm border border-[#003087] bg-[#FFC439] hover:bg-[#FFB800] text-[#003087] font-semibold text-sm tracking-wide transition-colors disabled:opacity-50"
                        data-testid={`plan-cta-paypal-${plan.id}`}
                    >
                        {paypalBusy ? "…" : (
                            <>
                                <PayPalLogo />
                                <span>Paga con PayPal</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

// Inline credit-card icon — neutral gray, matches our pill button.
function CardIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
        </svg>
    );
}

// Official PayPal wordmark — clean inline SVG using their published
// brand paths. Stays crisp at any size, no remote logo loaded.
function PayPalLogo() {
    return (
        <svg width="64" height="16" viewBox="0 0 124 33" fill="none" aria-hidden="true">
            <path fill="#003087" d="M46.211 6.749h-6.839a.95.95 0 0 0-.939.802l-2.766 17.537a.57.57 0 0 0 .564.658h3.265a.95.95 0 0 0 .939-.803l.746-4.73a.95.95 0 0 1 .938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746m.789 6.405c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 0 1 .563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.469 1.044.332 1.906m19.654-.079h-3.275a.57.57 0 0 0-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .562.66h2.95a.95.95 0 0 0 .939-.803l1.77-11.209a.568.568 0 0 0-.561-.658m-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883s-.671-1.402-.518-2.323c.296-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.7 1.418.558 2.339m22.007-6.374h-3.291a.95.95 0 0 0-.787.417l-4.539 6.686-1.924-6.425a.95.95 0 0 0-.912-.678h-3.234a.57.57 0 0 0-.541.754l3.625 10.638-3.408 4.811a.57.57 0 0 0 .465.9h3.287a.95.95 0 0 0 .781-.408l10.946-15.8a.57.57 0 0 0-.468-.895"/>
            <path fill="#009cde" d="M94.992 6.749h-6.84a.95.95 0 0 0-.938.802l-2.766 17.537a.57.57 0 0 0 .562.658h3.51a.665.665 0 0 0 .656-.562l.785-4.971a.95.95 0 0 1 .938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746m.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.57.57 0 0 1 .562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906m19.653-.079h-3.273a.57.57 0 0 0-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 0 0 .564.66h2.949a.95.95 0 0 0 .938-.803l1.771-11.209a.572.572 0 0 0-.565-.658m-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.668-1.402-.518-2.323.299-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.703 1.418.558 2.339m8.426-12.219-2.807 17.858a.57.57 0 0 0 .562.658h2.822c.469 0 .867-.34.939-.803l2.768-17.536a.57.57 0 0 0-.562-.659h-3.16a.57.57 0 0 0-.562.482"/>
            <path fill="#003087" d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.32.32 0 0 1 .314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.7 3.7 0 0 1 1.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.5 6.5 0 0 1-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 0 0-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.16.16 0 0 1-.096.035z"/>
            <path fill="#009cde" d="M23.048 7.667q-.042.268-.096.55c-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132l-1.422 9.017-.402 2.557a.703.703 0 0 0 .695.812h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.7 4.7 0 0 0-1.336-1.027z"/>
            <path fill="#012169" d="M21.754 7.151a10 10 0 0 0-1.203-.267 15 15 0 0 0-2.426-.177h-7.352a1.17 1.17 0 0 0-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 0 1 1.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545q.057-.282.096-.55a6.6 6.6 0 0 0-1.017-.429 9 9 0 0 0-.277-.087"/>
            <path fill="#003087" d="M9.614 7.699a1.17 1.17 0 0 1 1.159-.991h7.352c.871 0 1.684.057 2.426.177a10 10 0 0 1 1.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378 1.282 17.853.5 14.622.5h-9.38c-.66 0-1.223.48-1.325 1.133L.01 26.83a.806.806 0 0 0 .795.932h5.791l1.454-9.225z"/>
        </svg>
    );
}
